import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import OutlineButton from 'components/OutlineButton';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import Table from 'components/Table';

import { IUser } from 'utils/interfaces';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';
import UserModalContent from './ActionModalContent';
import InternalHeader from 'components/Navigation/InternalHeader';

export default function UsersPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState('');
  const [users, setUsers] = useState<Array<IUser>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<IUser | undefined>(undefined);

  const toggleUser = async (userId: string, isActive: boolean) => {
    setLoadingId(userId);
    let result: any = await generalApi.post('/toggle_my_user', {
      userId,
    });
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(isActive ? 'Usuario deshabilitado' : 'Usuario habilitado');
    setRefresh(true);
  };

  useEffect(() => {
    const getUsers = async () => {
      setLoading(true);
      const result = await generalApi.get('/my_users');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setUsers(result.data);
      setLoading(false);
    };
    if (refresh) {
      getUsers();
      setRefresh(false);
    }
  }, [generalApi, refresh]);

  return (
    <PrivateRoute>
      <>
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <UserModalContent
            title={actionData ? 'Editar usuario' : 'Crear usuario'}
            editData={actionData}
            onClose={() => {
              setIsActionOpen(false);
              setActionData(undefined);
            }}
            onSuccess={() => {
              setRefresh(true);
              setIsActionOpen(false);
              setActionData(undefined);
            }}
          />
        </Modal>
        <div className="min-h-full bg-gray-100 text-[#1B205A]">
          <InternalHeader />
          <div className="pb-8 pt-32 sm:pt-0 2xl:px-24">
            <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
              <p className="text-2xl font-bold">Lista de Usuarios</p>
              <div className="space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
                <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => setIsActionOpen(true)}>
                  Nuevo Usuario
                </OutlineButton>
                <OutlineButton
                  color={BUTTON_COLORS.BLUE}
                  onClick={() => history('/create_operation')}
                >
                  Nuevas Operaciones
                </OutlineButton>
              </div>
            </div>
            <Table
              isLoading={loading}
              loadingText="Buscando usuarios"
              columns={[
                {
                  title: 'Número de documento',
                  dataKey: 'number',
                },
                {
                  title: 'Nombres',
                  dataKey: 'name',
                },
                {
                  title: 'Apellidos',
                  dataKey: 'lname_p',
                  render: (item: IUser) => `${item.lname_p} ${item.lname_m}`,
                },
                {
                  title: 'Correo',
                  dataKey: 'email',
                },
                {
                  title: 'Opciones',
                  dataKey: 'options',
                  disableSort: true,
                  render: (item: IUser) => (
                    <div className="flex space-x-2 pr-2">
                      {/* <SolidButton
                       className='w-auto'
                        color={BUTTON_COLORS.BLUE}
                        onClick={() => {
                          setIsActionOpen(true);
                          setActionData(item);
                        }}
                      >
                        Editar
                      </SolidButton> */}
                      <SolidButton
                        className="w-auto"
                        isLoading={loadingId === item._id}
                        color={item.is_active ? BUTTON_COLORS.RED : BUTTON_COLORS.BLUE}
                        onClick={() => toggleUser(item._id, item.is_active)}
                      >
                        {item.is_active ? 'Deshabilitar' : 'Habilitar'}
                      </SolidButton>
                    </div>
                  ),
                },
              ]}
              data={users}
            />
          </div>
        </div>
      </>
    </PrivateRoute>
  );
}
